import { Muxer, ArrayBufferTarget } from 'mp4-muxer'

/**
 * Turn whatever a writer hands us into a short, small, upright clip.
 *
 * People upload from everywhere: a phone shooting 1080p60 HEVC at 23 Mbps, an
 * iMovie export that pillarboxed a vertical take into a 16:9 project so two
 * thirds of every frame is black, a landscape camera, a screen recording. This
 * normalises all of it to one thing the feed can play.
 *
 * Measured on real files: a 365 MB phone clip becomes ~13 MB, a 176 MB iMovie
 * export becomes ~7 MB. Both under ten seconds of work on a laptop.
 *
 * The rules, in order:
 *   1. Never keep more than MAX_SECONDS.
 *   2. Find the real picture inside the frame and throw away the black bars.
 *   3. Never upscale. A 608-wide source stays 608 wide -- inventing pixels
 *      costs bitrate and buys nothing.
 *   4. Portrait out, always. A landscape source is fitted, not cropped, so we
 *      never cut someone's head off to force a shape.
 */

export const MAX_SECONDS = 60

/**
 * The floor, and the more opinionated of the two numbers.
 *
 * A Collective clip is meant to be a semi-formed idea -- a chorus, a hook, the
 * bit that works -- not a teaser. Under twenty seconds a worship idea has not
 * had time to state a melody and land a line, and the feed fills with fragments
 * that tell a listener nothing about whether they like the writer. So this is
 * a refusal, not a warning: a shorter recording is sent back rather than
 * padded, because padding it with silence would be worse than saying no.
 */
export const MIN_SECONDS = 20
const TARGET_W = 720          // ceiling, never a floor
const TARGET_H = 1280
const FPS = 30
const AUDIO_BITRATE = 128_000

/**
 * How long a media file is, without decoding it.
 *
 * Needed before anything else on both clip paths -- the trimmer cannot draw a
 * strip for a length it does not know, and a file under MIN_SECONDS is turned
 * away rather than padded. Resolves NaN rather than rejecting: an unreadable
 * file is a message to the writer, not an exception to handle.
 */
export function mediaSeconds(file, kind = 'video') {
  return new Promise((res) => {
    const el = document.createElement(kind)
    el.preload = 'metadata'
    el.onloadedmetadata = () => { res(el.duration); URL.revokeObjectURL(el.src) }
    el.onerror = () => res(NaN)
    setTimeout(() => res(NaN), 8000)
    el.src = URL.createObjectURL(file)
  })
}

/** WebCodecs is required. Older browsers fall back to uploading the original. */
export function canEncode() {
  return typeof VideoEncoder !== 'undefined'
    && typeof VideoDecoder !== 'undefined'
    && typeof VideoFrame !== 'undefined'
}

/** Even dimensions only -- H.264 chroma subsampling requires it. */
const even = (n) => Math.max(2, Math.round(n / 2) * 2)

/**
 * Find the picture inside the frame.
 *
 * iMovie and friends bake black bars in rather than signalling them, so the
 * only way to know is to look. Scans one decoded frame inward from each edge
 * and stops at the first row/column that isn't near-black. Deliberately
 * conservative: if the scan finds almost nothing, or the result is a sliver, we
 * keep the whole frame rather than risk cropping real content.
 */
function detectContentBox(canvas) {
  const { width: w, height: h } = canvas
  const d = canvas.getContext('2d', { willReadFrequently: true })
    .getImageData(0, 0, w, h).data
  const DARK = 22
  const lit = (x, y) => {
    const i = (y * w + x) * 4
    return d[i] > DARK || d[i + 1] > DARK || d[i + 2] > DARK
  }
  // Sample every few pixels; a bar is uniform, so we don't need every one.
  const step = Math.max(1, Math.floor(Math.min(w, h) / 200))
  const rowLit = (y) => { for (let x = 0; x < w; x += step) if (lit(x, y)) return true; return false }
  const colLit = (x) => { for (let y = 0; y < h; y += step) if (lit(x, y)) return true; return false }

  let top = 0, bottom = h - 1, left = 0, right = w - 1
  while (top < bottom && !rowLit(top)) top++
  while (bottom > top && !rowLit(bottom)) bottom--
  while (left < right && !colLit(left)) left++
  while (right > left && !colLit(right)) right--

  const cw = right - left + 1
  const ch = bottom - top + 1
  // A near-black shot would otherwise crop to nothing.
  if (cw < w * 0.25 || ch < h * 0.25) return { x: 0, y: 0, w, h }
  return { x: left, y: top, w: cw, h: ch }
}

/**
 * Output size: portrait, capped, never upscaled, even numbers.
 *
 * The tolerance matters. A de-pillarboxed 608x1080 measures 0.5630 against a
 * 9:16 target of 0.5625 -- without slack it reads as "landscape", gets fitted
 * into a 9:16 frame, and we helpfully add back the very black bars we just
 * removed. Anything within a couple of percent of the target is vertical.
 */
function outputSize(box) {
  const srcAspect = box.w / box.h
  const outAspect = TARGET_W / TARGET_H // 9:16

  if (srcAspect <= outAspect * 1.02) {
    // Vertical (or taller). Fill the frame edge to edge -- no bars, no crop.
    let w = even(Math.min(box.w, TARGET_W))
    let h = even(w / srcAspect)
    if (h > TARGET_H) { h = TARGET_H; w = even(h * srcAspect) } // very tall sources
    return { w, h, fit: 'fill' }
  }

  // Genuinely landscape. Fit it inside a 9:16 frame rather than cropping a
  // face out of the middle -- a wide shot of a band has no safe centre.
  const w = even(Math.min(box.w, TARGET_W))
  return { w, h: even(w / outAspect), inner: { w, h: even(w / srcAspect) }, fit: 'letterbox' }
}

/**
 * Decode the source with a <video> element and read it frame by frame.
 *
 * Using the element rather than demuxing by hand means every container and
 * codec the browser can already play works for free -- .mov, HEVC, rotation
 * metadata, the lot.
 *
 * Frames are pulled by seeking to each output timestamp and drawing, rather
 * than by playing and using requestVideoFrameCallback. rVFC is faster in
 * principle but only fires while the element is actually compositing, so it
 * silently yields zero frames for a detached or hidden video -- which is
 * exactly how this runs. Seeking is deterministic, works detached, and gives
 * exact frame times. Measured at ~11ms per frame: about 20s for a 60s clip.
 */
async function encodeVideo(file, { start, end }, audio, onProgress, onPoster, signal) {
  const video = document.createElement('video')
  video.src = URL.createObjectURL(file)
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'

  await new Promise((res, rej) => {
    video.onloadedmetadata = res
    video.onerror = () => rej(new Error("That video couldn't be read."))
  })

  const seek = (t) => new Promise((res, rej) => {
    const ok = () => { cleanup(); res() }
    const bad = () => { cleanup(); rej(new Error("That video couldn't be read.")) }
    const cleanup = () => {
      video.removeEventListener('seeked', ok)
      video.removeEventListener('error', bad)
      clearTimeout(timer)
    }
    const timer = setTimeout(ok, 3000) // a seek that never lands must not hang
    video.addEventListener('seeked', ok)
    video.addEventListener('error', bad)
    video.currentTime = t
  })

  // First frame, to measure. Seek slightly in: frame zero is often a fade.
  await seek(Math.min(start + 0.25, video.duration - 0.05))

  const probe = document.createElement('canvas')
  probe.width = video.videoWidth
  probe.height = video.videoHeight
  probe.getContext('2d', { willReadFrequently: true }).drawImage(video, 0, 0)
  const box = detectContentBox(probe)
  const out = outputSize(box)

  // Hand back a still immediately. The encode takes fifteen-odd seconds, and
  // the difference between watching a spinner and watching your own clip sit
  // there with a bar under it is the whole perceived wait.
  if (onPoster) {
    try {
      const p = document.createElement('canvas')
      p.width = Math.round(out.w / 2)
      p.height = Math.round(out.h / 2)
      const pc = p.getContext('2d')
      if (out.fit === 'letterbox') {
        const iw = p.width
        const ih = Math.round(out.inner.h / 2)
        pc.fillStyle = '#000'
        pc.fillRect(0, 0, p.width, p.height)
        pc.drawImage(video, box.x, box.y, box.w, box.h, 0, (p.height - ih) / 2, iw, ih)
      } else {
        pc.drawImage(video, box.x, box.y, box.w, box.h, 0, 0, p.width, p.height)
      }
      onPoster(p.toDataURL('image/jpeg', 0.7))
    } catch { /* a poster is a nicety, never a reason to fail */ }
  }

  const canvas = new OffscreenCanvas(out.w, out.h)
  const ctx = canvas.getContext('2d')

  const target = new ArrayBufferTarget()
  // The audio track is declared only when we know we can fill it -- see
  // prepareAudio. An empty declared track has no decoder config and finalize()
  // throws on it.
  const muxer = new Muxer({
    target,
    video: { codec: 'avc', width: out.w, height: out.h },
    ...(audio ? { audio: { codec: 'aac', sampleRate: audio.rate, numberOfChannels: audio.chans } } : {}),
    fastStart: 'in-memory', // metadata first, so playback can start immediately
  })

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: () => {},
  })
  // ~0.07 bits per pixel per frame: transparent for this content and keeps a
  // 60s clip in single-digit megabytes.
  encoder.configure({
    codec: 'avc1.640028',
    width: out.w,
    height: out.h,
    bitrate: Math.round(out.w * out.h * FPS * 0.07),
    framerate: FPS,
    latencyMode: 'quality',
  })

  const duration = end - start
  const total = Math.max(1, Math.round(duration * FPS))
  let frames = 0
  const began = performance.now()

  const draw = () => {
    ctx.clearRect(0, 0, out.w, out.h)
    if (out.fit === 'letterbox') {
      const { w, h } = out.inner
      ctx.drawImage(video, box.x, box.y, box.w, box.h, 0, (out.h - h) / 2, w, h)
    } else {
      ctx.drawImage(video, box.x, box.y, box.w, box.h, 0, 0, out.w, out.h)
    }
  }
  const emit = (slot) => {
    const frame = new VideoFrame(canvas, {
      timestamp: Math.round((slot / FPS) * 1e6),
      duration: Math.round(1e6 / FPS),
    })
    encoder.encode(frame, { keyFrame: slot % (FPS * 2) === 0 })
    frame.close()
    frames++
  }
  const report = (done) => {
    const spent = performance.now() - began
    // Estimated from the measured rate, never guessed -- and withheld until
    // there is enough of a sample to extrapolate from. Half a second in and
    // one percent done, the honest arithmetic says "about 2951 seconds",
    // which is worse than saying nothing.
    const eta = (spent > 1200 && done > 0.04)
      ? Math.round(((spent / done) - spent) / 1000)
      : null
    onProgress?.(Math.min(0.99, done), eta)
  }

  /**
   * Fast path: let the video play and take frames as they are presented.
   *
   * Several times quicker than seeking, because the decoder runs flat out
   * instead of being restarted for every frame. It only works while the
   * element actually composites, so the element is in the document and we bail
   * to the seek path if no frame arrives -- which is exactly what happens in a
   * headless or backgrounded tab.
   */
  const byPlayback = () => new Promise((resolve) => {
    if (typeof video.requestVideoFrameCallback !== 'function') return resolve(false)
    Object.assign(video.style, {
      position: 'fixed', left: '0', top: '0', width: '2px', height: '2px',
      opacity: '0.01', pointerEvents: 'none', zIndex: '-1',
    })
    document.body.appendChild(video)

    const seen = new Set()
    let settled = false
    const finish = (ok) => {
      if (settled) return
      settled = true
      clearTimeout(firstFrame)
      video.pause()
      video.remove()
      resolve(ok)
    }
    // If nothing is presented promptly, this environment will never present.
    const firstFrame = setTimeout(() => finish(false), 1200)

    const onFrame = (_now, meta) => {
      if (settled) return
      if (signal?.aborted) return finish(true)
      clearTimeout(firstFrame)
      const t = meta.mediaTime
      if (t >= end) return finish(true)
      if (t >= start) {
        const slot = Math.round((t - start) * FPS)
        if (slot < total && !seen.has(slot)) {
          seen.add(slot)
          draw()
          emit(slot)
          report(seen.size / total)
        }
      }
      video.requestVideoFrameCallback(onFrame)
    }

    video.onended = () => finish(true)
    video.requestVideoFrameCallback(onFrame)
    video.playbackRate = 4 // browsers clamp this; whatever we are given, we take
    video.play().catch(() => finish(false))
  })

  await seek(start)
  const fast = await byPlayback()

  if (!fast) {
    // Slow path: seek to each frame. Deterministic and works detached, at
    // roughly 11ms a frame.
    for (let i = 0; i < total; i++) {
      if (signal?.aborted) break
      const t = start + i / FPS
      if (t >= video.duration) break
      await seek(t)
      draw()
      emit(i)
      // Encoding runs behind the seeks; don't let the queue grow without bound.
      if (encoder.encodeQueueSize > 12) await new Promise((r) => setTimeout(r, 0))
      report((i + 1) / total)
    }
  }

  await encoder.flush()
  encoder.close()
  URL.revokeObjectURL(video.src)
  if (!frames) throw new Error("That video couldn't be read.")
  return { muxer, target, out, box, frames, sourceSize: { w: probe.width, h: probe.height } }
}

/**
 * Decode the audio before anything is muxed.
 *
 * The muxer wants to know at construction time whether there is an audio track
 * at all -- declaring one and then failing to feed it leaves the track without
 * a decoder config and finalize() dies. So this runs first and returns null for
 * "no usable audio", which is a normal outcome: a silent clip beats no clip.
 */
async function prepareAudio(file, { start, end }) {
  if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') return null
  let decoded
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    decoded = await ctx.decodeAudioData(await file.arrayBuffer())
    ctx.close()
  } catch {
    return null
  }
  const rate = decoded.sampleRate
  const chans = Math.min(decoded.numberOfChannels, 2)
  const from = Math.floor(start * rate)
  const to = Math.min(decoded.length, Math.floor(end * rate))
  if (to <= from || !rate || !chans) return null

  const support = await AudioEncoder.isConfigSupported({
    codec: 'mp4a.40.2', sampleRate: rate, numberOfChannels: chans, bitrate: AUDIO_BITRATE,
  }).catch(() => null)
  if (!support?.supported) return null

  return { decoded, rate, chans, from, to }
}

async function encodeAudio(prepared, muxer) {
  const { decoded, rate, chans, from, to } = prepared
  const enc = new AudioEncoder({
    output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
    error: () => {},
  })
  enc.configure({ codec: 'mp4a.40.2', sampleRate: rate, numberOfChannels: chans, bitrate: AUDIO_BITRATE })


  const BLOCK = 4096
  for (let i = from; i < to; i += BLOCK) {
    const len = Math.min(BLOCK, to - i)
    const interleaved = new Float32Array(len * chans)
    for (let c = 0; c < chans; c++) {
      const src = decoded.getChannelData(Math.min(c, decoded.numberOfChannels - 1))
      for (let s = 0; s < len; s++) interleaved[s * chans + c] = src[i + s]
    }
    enc.encode(new AudioData({
      format: 'f32',
      sampleRate: rate,
      numberOfFrames: len,
      numberOfChannels: chans,
      timestamp: Math.round(((i - from) / rate) * 1e6),
      data: interleaved,
    }))
  }
  await enc.flush()
  enc.close()
}

/**
 * The one entry point.
 *
 * Returns a File ready to upload plus what happened to it, or null if this
 * browser can't encode -- callers fall back to the original file.
 */
export async function makeClip(file, { start = 0, end = null, onProgress, onPoster, signal } = {}) {
  if (!canEncode()) return null

  const probe = document.createElement('video')
  probe.src = URL.createObjectURL(file)
  probe.preload = 'metadata'
  const dur = await new Promise((res) => {
    probe.onloadedmetadata = () => res(probe.duration)
    probe.onerror = () => res(NaN)
    setTimeout(() => res(NaN), 8000)
  })
  URL.revokeObjectURL(probe.src)
  if (!Number.isFinite(dur) || dur <= 0) return null

  // Same floor as the still path, and refused here for the same reason: a
  // twelve-second fragment in the feed teaches a listener nothing about the
  // writer, which is the only thing the feed is for.
  if (dur < MIN_SECONDS) {
    throw new Error(
      `That clip is ${Math.round(dur)} seconds. The Collective needs at least `
      + `${MIN_SECONDS} — long enough for someone to hear the idea.`,
    )
  }

  const s = Math.max(0, Math.min(start, dur - 0.5))
  const e = Math.min(end ?? s + MAX_SECONDS, dur, s + MAX_SECONDS)

  const audio = await prepareAudio(file, { start: s, end: e })
  const { muxer, target, out, box, sourceSize } =
    await encodeVideo(file, { start: s, end: e }, audio, onProgress, onPoster, signal)
  if (audio) await encodeAudio(audio, muxer)
  muxer.finalize()
  onProgress?.(1)

  const name = (file.name || 'clip').replace(/\.[^.]+$/, '') + '.mp4'
  const out_file = new File([target.buffer], name, { type: 'video/mp4' })
  return {
    file: out_file,
    from: file.size,
    to: out_file.size,
    seconds: Math.round(e - s),
    width: out.w,
    height: out.h,
    // True when we found and removed baked-in black bars.
    debarred: box.w !== sourceSize.w || box.h !== sourceSize.h,
  }
}


/**
 * A still image plus a recording, encoded as a clip.
 *
 * The path that matters most in practice: most writers will never film
 * themselves, and a voice memo over a photograph is a real thing they already
 * have. Rather than teaching the feed a second kind of row -- an image here, an
 * audio object there, two code paths on the playback side forever -- this
 * renders the pair into exactly the same mp4 a filmed clip produces. Downstream
 * (song_clips, r2-playback-url, the feed) nothing knows the difference.
 *
 * Six frames a second, not thirty. The picture never changes, so the extra
 * frames encode to almost nothing but still cost real encoder time on a phone.
 */
const STILL_FPS = 6

export async function makeStillClip(image, audioFile, { onProgress, signal } = {}) {
  if (!canEncode()) return null

  const bitmap = await createImageBitmap(image).catch(() => null)
  if (!bitmap) throw new Error("That image couldn't be read.")

  // Portrait, always, and cover-fit: a landscape photo is cropped to the middle
  // rather than letterboxed, because the alternative is a phone screen that is
  // two thirds black.
  const out = { w: TARGET_W, h: TARGET_H }
  const scale = Math.max(out.w / bitmap.width, out.h / bitmap.height)
  const dw = bitmap.width * scale
  const dh = bitmap.height * scale
  const canvas = new OffscreenCanvas(out.w, out.h)
  const ctx = canvas.getContext('2d')
  ctx.drawImage(bitmap, (out.w - dw) / 2, (out.h - dh) / 2, dw, dh)
  bitmap.close()

  // The recording decides the length; the picture has no opinion about it.
  const probe = new Audio()
  probe.src = URL.createObjectURL(audioFile)
  probe.preload = 'metadata'
  const dur = await new Promise((res) => {
    probe.onloadedmetadata = () => res(probe.duration)
    probe.onerror = () => res(NaN)
    setTimeout(() => res(NaN), 8000)
  })
  URL.revokeObjectURL(probe.src)
  if (!Number.isFinite(dur) || dur <= 0) throw new Error("That recording couldn't be read.")
  if (dur < MIN_SECONDS) {
    throw new Error(
      `That recording is ${Math.round(dur)} seconds. A Collective clip needs at `
      + `least ${MIN_SECONDS} — long enough for someone to hear the idea.`,
    )
  }
  const end = Math.min(dur, MAX_SECONDS)

  const audio = await prepareAudio(audioFile, { start: 0, end })
  if (!audio) throw new Error("That recording couldn't be read.")

  const target = new ArrayBufferTarget()
  const muxer = new Muxer({
    target,
    video: { codec: 'avc', width: out.w, height: out.h },
    audio: { codec: 'aac', sampleRate: audio.rate, numberOfChannels: audio.chans },
    fastStart: 'in-memory',
  })

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: () => {},
  })
  encoder.configure({
    codec: 'avc1.640028',
    width: out.w,
    height: out.h,
    // A static picture needs a fraction of what moving footage does.
    bitrate: Math.round(out.w * out.h * STILL_FPS * 0.02),
    framerate: STILL_FPS,
    latencyMode: 'quality',
  })

  const total = Math.max(1, Math.round(end * STILL_FPS))
  for (let i = 0; i < total; i++) {
    if (signal?.aborted) break
    const frame = new VideoFrame(canvas, {
      timestamp: Math.round((i / STILL_FPS) * 1e6),
      duration: Math.round(1e6 / STILL_FPS),
    })
    encoder.encode(frame, { keyFrame: i % (STILL_FPS * 2) === 0 })
    frame.close()
    if (encoder.encodeQueueSize > 12) await new Promise((r) => setTimeout(r, 0))
    onProgress?.(Math.min(0.99, (i + 1) / total))
  }
  await encoder.flush()
  encoder.close()

  await encodeAudio(audio, muxer)
  muxer.finalize()
  onProgress?.(1)

  const name = (image.name || 'clip').replace(/\.[^.]+$/, '') + '.mp4'
  const file = new File([target.buffer], name, { type: 'video/mp4' })
  return {
    file,
    from: image.size + audioFile.size,
    to: file.size,
    seconds: Math.round(end),
    width: out.w,
    height: out.h,
    debarred: false,
  }
}
